import {Button, IconButton, withWidth} from '@material-ui/core';
import {WithWidth} from '@material-ui/core/withWidth';
import {showModal} from 'Core/ducks/modal';
import {toEither, isLoading} from 'Core/models/Async';
import * as EnrollmentFileActions from 'Enrollment/ducks/enrollmentFileProcessing';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {fold as foldEither} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {connect, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import {renderAsync} from 'Shared/components/RenderAsync';
import TableHeader from 'Shared/components/Table/TableHeader';
import './Dashboard.sass';
import EnrollmentProcessingAccuracyChart from './EnrollmentProcessingAccuracyChart';
import {Grid} from '@material-ui/core';
import DashboardCard from './DashboardCard';
import DashboardCardError from './DashboardCardError';
import LoadingDashboardCard from './LoadingDashboardCard';
import EnrollmentFileVolumeAndTurnAroundChart from './EnrollmentFileVolumeAndTurnAroundChart';
import EnrollmentDaysToCompleteTurnAroundChart from './EnrollmentDaysToCompleteTurnAroundChart';
import EnrollmentTotalRecordsChart from './EnrollmentTotalRecordsChart';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import FilterComponent from 'Shared/components/FilterComponent/FilterComponent';

type DashboardProps = {
  showModal: typeof showModal;
} & typeof EnrollmentFileActions &
  ReturnType<typeof mapStateToProps> &
  WithWidth &
  RouteComponentProps;

export const Dashboard = (props: DashboardProps) => {
  const scopes = useSelector(selectUserScopes);
  const WeeklyChart = 'Weekly';
  const {
    getEnrollmentMetrics,
    getFilesNotProcessedMetrics,
    getFileFeedbackMetrics,
    getFileProfileMetrics,
    getAccountProfileMetrics,
    getFileTrackingMonitorMetrics,
    getEnrollmentFiles,
    getLifeCycleReportChartDetails,
    getProcessingAccuracyChartDetails,
    getDaysToCompleteTurnAroundChartDetails,
    getTotalRecordsChartDetails,
    getBrokerSpreadsheetUploadsMetrics,
  } = props;

  useEffect(() => {
    getBrokerSpreadsheetUploadsMetrics();
    getEnrollmentMetrics();
    getFilesNotProcessedMetrics();
    getFileFeedbackMetrics();
    getFileProfileMetrics();
    getAccountProfileMetrics();
    getFileTrackingMonitorMetrics();
    getLifeCycleReportChartDetails(WeeklyChart);
    getProcessingAccuracyChartDetails();
    getDaysToCompleteTurnAroundChartDetails();
    getTotalRecordsChartDetails();
    getEnrollmentFiles(undefined, {
      ...EnrollmentFileActions.EMWEmptyEnrollmentFileFilter,
      pageSize: 1500,
    });
  }, [
    getBrokerSpreadsheetUploadsMetrics,
    getEnrollmentMetrics,
    getFilesNotProcessedMetrics,
    getFileFeedbackMetrics,
    getFileProfileMetrics,
    getAccountProfileMetrics,
    getFileTrackingMonitorMetrics,
    getEnrollmentFiles,
    getLifeCycleReportChartDetails,
    getProcessingAccuracyChartDetails,
    getDaysToCompleteTurnAroundChartDetails,
    getTotalRecordsChartDetails,
  ]);
  const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  
  return (
    <div className="enrollment-dashboard fade">
      <div className="enrollment-dashboard__header">
        <TableHeader
          id="enrollment-dashboard-title"
          className="enrollment-dashboard__dashboard-title"
          title="Dashboard"
        />
        <div >
          <Button 
            variant="contained" 
            color="primary" 
            disableElevation
            onClick={() => setIsColumnDrawerOpen(true)}
          >
            <i className="fa fa-sliders-h"></i>
            <span className="btn-title">Search Filters</span>
          </Button>
        </div>
      </div>

      <>
        <FilterComponent
          open={isColumnDrawerOpen}
          onClose={() => setIsColumnDrawerOpen(false)}
        />
      </>

      <div className="enrollment-dashboard__scope-categories">
        <div className="scope-category">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <div className="enrollment-dashboard__enrollment-charts">
                <Grid container spacing={6}>
                  <RequireScope scopes={scopes} scope={Scope.ViewProcessingAccuracyChart}>
                    <Grid item xs={12} md={6} xl={6}>
                      <div className="enrollment-dashboard__days-taken-charts">
                        {pipe(
                          toEither(props.processingAccuracyDetails),
                          foldEither(renderAsync, processingAccuracyDetails => (
                            <EnrollmentProcessingAccuracyChart
                              processingAccuracyChartDetails={processingAccuracyDetails}
                            />
                          ))
                        )}
                      </div>
                    </Grid>
                  </RequireScope>
                  <RequireScope scopes={scopes} scope={Scope.ViewLifeCycleReportChart}>
                    <Grid item xs={12} md={6} xl={6}>
                      {pipe(
                        toEither(props.lifeCycleReportDetails),
                        foldEither(renderAsync, lifeCycleReportDetailData => (
                          <EnrollmentFileVolumeAndTurnAroundChart
                            lifeCycleReportChartDetails={lifeCycleReportDetailData}
                          />
                        ))
                      )}
                    </Grid>
                  </RequireScope>
                  <RequireScope scopes={scopes} scope={Scope.ViewDaysToCompleteTurnAroundChart}>
                    <Grid item xs={12} md={6}>
                      {pipe(
                        toEither(props.daysToCompleteTurnAroundDetails),
                        foldEither(renderAsync, daysToCompleteTurnAroundDetailData => (
                          <EnrollmentDaysToCompleteTurnAroundChart
                            daysToCompleteTurnAroundChartDetails={daysToCompleteTurnAroundDetailData}
                          />
                        ))
                      )}
                    </Grid>
                  </RequireScope>
                  <RequireScope scopes={scopes} scope={Scope.ViewTotalRecordsChart}>
                    <Grid item xs={12} md={6}>
                      {pipe(
                        toEither(props.totalRecordsDetails),
                        foldEither(renderAsync, totalRecordsDetailData => (
                          <EnrollmentTotalRecordsChart totalRecordsChartDetails={totalRecordsDetailData} />
                        ))
                      )}
                    </Grid>
                  </RequireScope>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
        <div className="scope-category scope-category-file-processing">
          <h2 id="enrollment-dashboard-title-enrollment-processing" className="scope-category-title">
            Enrollment Processing
          </h2>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <div className="enrollment-dashboard__enrollment-cards">
                <Grid container spacing={4}>
                  {pipe(
                    toEither(props.enrollmentMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="Enrollment Files Reporting"
                            iconName="lifecycle-report"
                            subTitles={['esr', 'emw']}
                            id="lifecycle-reporting"
                          />
                        ) : (
                          <DashboardCardError
                            title="Enrollment Files Reporting"
                            iconName="lifecycle-report"
                            id="lifecycle-reporting"
                          />
                        ),
                      metrics => (
                        <DashboardCard
                          title="Enrollment Files Reporting"
                          iconName="lifecycle-report"
                          cardContents={[{subTitle: 'esr', count: metrics.esrMetrics.totalCases}]}
                          onClick={() => props.history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING)}
                          id="lifecycle-reporting"
                        />
                      )
                    )
                  )}

                  {pipe(
                    toEither(props.enrollmentMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="Enrollment File Errors"
                            iconName="file-errors"
                            subTitles={['Sent', 'Not Sent']}
                            id="files-errors-loading"
                          />
                        ) : (
                          <DashboardCardError
                            title="Enrollment File Errors"
                            iconName="file-errors"
                            id="files-errors-errors"
                          />
                        ),
                      metrics => (
                        <DashboardCard
                          title="Enrollment File Errors"
                          iconName="file-errors"
                          cardContents={[
                            {subTitle: 'Sent', count: metrics.enrollTrackRejectedFiles.emailSentForRejectedApps},
                            {subTitle: 'Not Sent', count: metrics.enrollTrackRejectedFiles.emailNotSentForRejectedApps},
                          ]}
                          onClick={() => props.history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS)}
                          id="files-errors"
                        />
                      )
                    )
                  )}

                  {pipe(
                    toEither(props.filesNotProcessedMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="Files Not Processed"
                            iconName="files-not-processed"
                            subTitles={['Count']}
                            id="files-not-processed-loading"
                          />
                        ) : (
                          <DashboardCardError
                            title="Files Not Processed"
                            iconName="files-not-processed"
                            id="files-not-processed-error"
                          />
                        ),
                      metrics => (
                        <DashboardCard
                          title="Files Not Processed"
                          iconName="files-not-processed"
                          cardContents={[{subTitle: 'Count', count: metrics.filesNotProcessed}]}
                          onClick={() => props.history.push(EnrollmentRouteConstants.ENROLLMENT_FILES_NOT_PROCESSED)}
                          id="files-not-processed"
                        />
                      )
                    )
                  )}

                  {pipe(
                    toEither(props.fileFeedbackMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="File Feedback"
                            iconName="file-feedback"
                            subTitles={['Coming Soon']}
                            id="file-feedback-loading"
                          />
                        ) : (
                          <DashboardCardError title="File Feedback" iconName="file-feedback" id="file-feedback-error" />
                        ),
                      metrics => (
                        <DashboardCard
                          title="File Feedback"
                          iconName="file-feedback"
                          cardContents={[
                            {subTitle: 'Sent', count: metrics.fileFeedback.sent!},
                            {subTitle: 'Not Sent', count: metrics.fileFeedback.unSent!},
                          ]}
                          id="file-feedback"
                        />
                      )
                    )
                  )}
                  <RequireScope scopes={scopes} scope={Scope.EnrollTrackAdministrator}>
                    {pipe(
                      toEither(props.brokerSpreadsheetUploads),
                      foldEither(
                        loadingOrError =>
                          isLoading(loadingOrError) ? (
                            <LoadingDashboardCard
                              title="Broker Spreadsheet Uploads"
                              iconName="broker-spreadsheet-uploads"
                              subTitles={['Recv', 'Proc', 'Cmpl']}
                              id="broker-spreadsheet-uploads-loading"
                            />
                          ) : (
                            <DashboardCardError
                              title="Broker Spreadsheet Uploads"
                              iconName="broker-spreadsheet-uploads"
                              id="broker-spreadsheet-uploads-error"
                            />
                          ),
                        metrics => (
                          <DashboardCard
                            title="Broker Spreadsheet Uploads"
                            iconName="broker-spreadsheet-uploads"
                            cardContents={[
                              {subTitle: 'Recv', count: metrics.receivedFilesCount!},
                              {subTitle: 'Proc', count: metrics.processingFilesCount!},
                              {subTitle: 'Cmpl', count: metrics.completedFilesCount!},
                            ]}
                            onClick={() => props.history.push(EnrollmentRouteConstants.EXTERNAL_UPLOAD_QUEUE)}
                            id="broker-spreadsheet-uploads"
                          />
                        )
                      )
                    )}
                  </RequireScope>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className="scope-category scope-category-client-onboarding">
          <h2 className="scope-category-title">Account/File Profile</h2>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <div className="enrollment-dashboard__enrollment-cards">
                <Grid container spacing={4}>
                  {pipe(
                    toEither(props.accountProfileMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="Account Profile"
                            iconName="account-profile"
                            subTitles={['Count']}
                            id="account-profile-loading"
                          />
                        ) : (
                          <DashboardCardError
                            title="Account Profile"
                            iconName="account-profile"
                            id="account-profile-error"
                          />
                        ),
                      metrics => (
                        <DashboardCard
                          title="Account Profile"
                          iconName="account-profile"
                          cardContents={[{subTitle: 'Count', count: metrics.totalAccountProfile}]}
                          id="account-profile"
                        />
                      )
                    )
                  )}

                  {pipe(
                    toEither(props.fileProfileMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="File Profile"
                            iconName="file-profile"
                            subTitles={['Count']}
                            id="file-profile-loading"
                          />
                        ) : (
                          <DashboardCardError title="File Profile" iconName="file-profile" id="file-profile-error" />
                        ),
                      metrics => (
                        <DashboardCard
                          title="File Profile"
                          iconName="file-profile"
                          cardContents={[{subTitle: 'Count', count: metrics.totalFileProfile}]}
                          id="file-profile"
                        />
                      )
                    )
                  )}

                  {pipe(
                    toEither(props.fileTrackingMonitorMetrics),
                    foldEither(
                      loadingOrError =>
                        isLoading(loadingOrError) ? (
                          <LoadingDashboardCard
                            title="File Tracking"
                            iconName="file-tracking"
                            subTitles={['Count']}
                            id="file-tracking-loading"
                          />
                        ) : (
                          <DashboardCardError title="File Tracking" iconName="file-tracking" id="file-tracking-error" />
                        ),
                      metrics => (
                        <DashboardCard
                          title="File Tracking"
                          iconName="file-tracking"
                          cardContents={[{subTitle: 'Count', count: metrics.totalFileTrackingMonitor}]}
                          id="file-tracking"
                        />
                      )
                    )
                  )}
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  enrollmentMetrics: state.enrollmentFileProcessing.enrollmentMetrics,
  enrollmentFiles: state.enrollmentFileProcessing.enrollmentFiles,
  filesNotProcessedMetrics: state.enrollmentFileProcessing.filesNotProcessedMetrics,
  fileFeedbackMetrics: state.enrollmentFileProcessing.fileFeedbackMetrics,
  fileProfileMetrics: state.enrollmentFileProcessing.fileProfileMetrics,
  accountProfileMetrics: state.enrollmentFileProcessing.accountProfileMetrics,
  fileTrackingMonitorMetrics: state.enrollmentFileProcessing.fileTrackingMonitorMetrics,
  lifeCycleReportDetails: state.enrollmentFileProcessing.lifeCycleReportChartDetails,
  processingAccuracyDetails: state.enrollmentFileProcessing.processingAccuracyChartDetails,
  daysToCompleteTurnAroundDetails: state.enrollmentFileProcessing.daysToCompleteTurnAroundChartDetails,
  totalRecordsDetails: state.enrollmentFileProcessing.totalRecordsChartDetails,
  brokerSpreadsheetUploads: state.enrollmentFileProcessing.brokerSpreadsheetUploadsMetrics,
});

export default withRouter(
  connect(mapStateToProps, {
    ...EnrollmentFileActions,
  })(withWidth()(Dashboard))
);
